import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@components/ui/dialog';
import { InternalScheduleDialogProps } from './types';
import { useTranslation } from 'react-i18next';
import { useEffect, useRef, useState } from 'react';
import { Form, FormField } from '@components/ui/form';
import { FormItemWrapper } from '@components/ui/form/itemWrapper';
import { Input } from '@components/ui/form/input';
import { Checkbox } from '@components/ui/checkbox';
import Scheduler from '@components/scheduler';
import { RemoveScheduleDialog } from '../remove';
import { SchedulerDate } from '@components/scheduler/date';
import { toTenantDate } from '@utils/timeZone';
import { MultiSelect } from '@components/ui/form/multiSelect';
import { useSchedulerMaxHeight } from '@hooks/scheduler/maxHeight';
import { Loader } from '@components/loader';
import { useInternalScheduler } from '@hooks/scheduler/internal';
import { SpecialtyOverrideDialog } from './action/specialty';
import { OverlapScheduleDialog } from './action/overlap';
import { InternalScheduleActions } from './actions';
import { SchedulerSidebar } from '@components/scheduler/sidebar';
import { Button } from '@components/ui/button';
import { SvgConsumer } from '@components/icons/svgConsumer';
import { formatTime, shortDate, shortDayDate } from '@utils/i18n';
import { cn } from '@utils/cn';
import { scrollActiveScheduleIntoView } from './helpers';
import { InternalScheduleMultiDayInputs } from './inputs/multiDay';
import { InternalScheduleExtendDaysInputs } from './inputs/extendDays';
import { InternalScheduleHeaderPending } from './inputs/pending';
import { TimeRangePicker } from '@components/scheduling/timeRangePicker';
import { SeriesCalendarLegend } from './seriesCalendar';
import { seriesDayCalendarStyle } from './seriesCalendar/constants';

export default function InternalScheduleDialog({
  open,
  setOpen,
  type,
  schedule,
  jobId,
  initialDate,
  activeId,
  readOnly,
  initialParams = {},
}: InternalScheduleDialogProps) {
  const { t } = useTranslation('app');
  const popoverRef = useRef<HTMLDivElement>(null);
  const gridScrollRef = useRef<HTMLDivElement>(null);
  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const maxHeight = useSchedulerMaxHeight(popoverRef);
  const {
    form,
    onSubmit,
    locked,
    data,
    schedulesFetching,
    schedulesLoading,
    removeSchedule,
    remove,
    setRemove,
    overlap,
    setOverlap,
    conflicts,
    specialtyOverride,
    setSpecialtyOverride,
    selectedSpecialties,
    setSelectedSpecialties,
    selectedForemen,
    setSelectedForemen,
    allUsers,
    setParam,
    specialties,
    fieldUsers,
    rawDate,
    setRawDate,
    active,
    jobSubtitle,
    canScheduleMultiDay,
    canExtendMultiDay,
    multiDayPosition,
    weekendDefaults,
    seriesRange,
    calendarModifiers,
    showSeriesCalendar,
    seriesPending,
    timeRange,
    setActiveTime,
    calendarOpen,
    setCalendarOpen,
  } = useInternalScheduler({
    open,
    setOpen,
    type,
    schedule,
    jobId,
    initialDate,
    readOnly,
    initialParams,
  });
  const { setValue } = form;

  useEffect(() => {
    if (!open || !active.fresh) return;
    const frame = requestAnimationFrame(() => {
      if (gridScrollRef.current) scrollActiveScheduleIntoView(gridScrollRef.current);
    });
    return () => cancelAnimationFrame(frame);
  }, [open, active.fresh, active.schedule.id, active.schedule.foreman?.id]);

  const dateControl = (
    <SchedulerDate
      date={rawDate}
      setDate={setRawDate}
      loading={Boolean(schedulesFetching)}
      calendar={{
        open: calendarOpen,
        setOpen: setCalendarOpen,
        ...(showSeriesCalendar && {
          modifiers: calendarModifiers,
          modifiersClassNames: seriesDayCalendarStyle,
          legend: <SeriesCalendarLegend modifiers={calendarModifiers} />,
        }),
      }}
    />
  );
  const timeControl = (
    <TimeRangePicker
      id="scheduler-time"
      value={timeRange}
      onChange={setActiveTime}
      disabled={readOnly || !active.schedule.id || !timeRange}
    />
  );
  const dateCaption = multiDayPosition && (
    <button
      type="button"
      className="text-xs text-neutral-500 underline-offset-2 hover:text-neutral-800 hover:underline"
      onClick={() => setCalendarOpen(true)}>
      {[
        t('schedule.multiDay.dayOf', multiDayPosition),
        seriesRange && [...new Set(seriesRange.map(shortDate))].join(' – '),
      ]
        .filter(Boolean)
        .join(' · ')}
    </button>
  );
  const schedulerStyle = maxHeight > 0 ? { height: maxHeight } : undefined;

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          ref={popoverRef}
          className="w-[calc(100%-3rem)] gap-0 overflow-hidden p-0"
          onEscapeKeyDown={(e) => {
            if (document.activeElement?.hasAttribute('data-revert-on-escape')) e.preventDefault();
          }}>
          <DialogHeader className="scheduler-navigation sticky top-0 z-10 grid grid-cols-[1fr_auto_1fr] items-start gap-6 space-y-0 bg-white p-6 shadow-md">
            <div className="flex flex-col gap-0.5">
              <DialogTitle className="h-fit">
                {t('schedule.team', { team: type?.name })}
              </DialogTitle>
              {jobSubtitle && <DialogDescription>{jobSubtitle}</DialogDescription>}
            </div>
            <div className="flex flex-col items-center gap-3">
              {seriesPending ? (
                <InternalScheduleHeaderPending dateControl={dateControl} />
              ) : canScheduleMultiDay ? (
                <Form {...form}>
                  <InternalScheduleMultiDayInputs
                    startDate={rawDate}
                    disabled={locked}
                    weekendDefaults={weekendDefaults}
                    popoverRef={popoverRef}
                    dateControl={dateControl}
                    dateCaption={dateCaption}
                  />
                </Form>
              ) : canExtendMultiDay ? (
                <Form {...form}>
                  <InternalScheduleExtendDaysInputs
                    disabled={locked}
                    weekendDefaults={weekendDefaults}
                    dateControl={dateControl}
                    dateCaption={dateCaption}
                  />
                </Form>
              ) : (
                <div className="flex flex-col items-center">
                  {dateControl}
                  {dateCaption}
                </div>
              )}
              {timeControl}
            </div>
            <div />
          </DialogHeader>
          <div
            className="mt-3 flex min-h-0 min-w-0 items-stretch overflow-hidden"
            style={schedulerStyle}>
            <div className="flex min-h-0 flex-1 flex-col pl-6">
              <div className="flex items-center justify-between gap-4 pb-3 pr-6 pt-1.5">
                {!readOnly ? (
                  <Form {...form}>
                    <div className="flex flex-wrap items-center gap-4">
                      <MultiSelect
                        placeholder={t('filters.teams.placeholder')}
                        className="w-fit whitespace-nowrap"
                        options={specialties.options.map((el) => ({
                          value: el.value,
                          label: el.name,
                        }))}
                        value={selectedSpecialties}
                        limit={1}
                        onChange={setSelectedSpecialties}
                        disabled={specialties.disabled}
                        side="bottom"
                        popoverRef={popoverRef}
                      />
                      <MultiSelect
                        placeholder={t('filters.foremen.placeholder')}
                        className="w-fit whitespace-nowrap"
                        options={fieldUsers.options
                          .filter((el) =>
                            selectedForemen.length
                              ? !data?.records.some(
                                  (record) =>
                                    record.id === el.id && !selectedForemen.includes(el.id),
                                )
                              : true,
                          )
                          .map((el) => ({
                            value: el.id,
                            label: el.fullName || '',
                          }))}
                        value={selectedForemen}
                        limit={1}
                        onChange={setSelectedForemen}
                        disabled={fieldUsers.disabled}
                        side="bottom"
                        popoverRef={popoverRef}
                      />
                      <Checkbox
                        id="allDivisions"
                        checked={allUsers === 'true'}
                        onCheckedChange={() =>
                          setParam('allUsers', allUsers === 'true' ? '' : 'true')
                        }>
                        {t('schedules.allDivisions')}
                      </Checkbox>
                    </div>
                  </Form>
                ) : (
                  <div />
                )}
                <Button
                  variant="ghost"
                  className="h-fit shrink-0 gap-1.5 p-1 text-xs text-neutral-700 hover:bg-transparent hover:text-neutral-900"
                  onClick={() => setSidebarExpanded(!sidebarExpanded)}>
                  <span
                    className={cn(
                      'flex shrink-0 items-center text-xs',
                      !sidebarExpanded && '-scale-x-100',
                    )}>
                    <SvgConsumer id="arrow" />
                    <SvgConsumer id="arrow" className="-ml-1.5" />
                  </span>
                  {sidebarExpanded ? t('actions.hide') : t('actions.show')}
                </Button>
              </div>
              <div
                ref={gridScrollRef}
                className="scheduler-container scrollbar-thin relative min-h-0 flex-1 overflow-y-auto overflow-x-hidden pr-6">
                {schedulesLoading && <Loader local />}
                <div className={cn(schedulesLoading && 'invisible')} aria-busy={schedulesLoading}>
                  <Scheduler
                    modal
                    date={rawDate}
                    foremen={data?.records}
                    withoutTeam={data?.withoutTeam}
                    outOfScopeRecords={data?.outOfScopeRecords}
                    schedule={schedule}
                    activeId={activeId}
                    restrictTo="schedulerGrid"
                  />
                </div>
              </div>
            </div>
            {sidebarExpanded && (
              <div className="flex min-h-0 min-w-[300px] max-w-[35%] shrink grow-0 basis-[420px] flex-col">
                <SchedulerSidebar />
              </div>
            )}
          </div>
          <div ref={popoverRef} className="overflow-visible" />
          <Form {...form}>
            <form
              className="dialog-footer border-t p-6"
              // eslint-disable-next-line @typescript-eslint/no-misused-promises
              onSubmit={form.handleSubmit(onSubmit)}>
              <FormField
                control={form.control}
                name="forceOverlapping"
                render={() => (
                  <FormItemWrapper>
                    <input type="hidden" />
                  </FormItemWrapper>
                )}
              />
              <FormField
                control={form.control}
                name="foremanId"
                render={({ field }) => (
                  <FormItemWrapper>
                    <input type="hidden" {...field} />
                  </FormItemWrapper>
                )}
              />
              <FormField
                control={form.control}
                name="startsAt"
                render={({ field }) => (
                  <FormItemWrapper>
                    <input type="hidden" {...field} />
                  </FormItemWrapper>
                )}
              />
              <FormField
                control={form.control}
                name="endsAt"
                render={({ field }) => (
                  <FormItemWrapper>
                    <input type="hidden" {...field} />
                  </FormItemWrapper>
                )}
              />
              <FormField
                control={form.control}
                name="note"
                render={({ field }) => (
                  <FormItemWrapper label={t('forms.note.label')}>
                    <Input disabled={readOnly} {...field} />
                  </FormItemWrapper>
                )}
              />
              <div className="flex flex-col gap-2">
                <div className="mt-6 flex items-start justify-end gap-2">
                  {!readOnly && (
                    <div className="flex h-10 flex-1 items-center justify-between gap-4">
                      <div>
                        {schedule?.id && schedule.id !== 'new' && (
                          <Button
                            variant="dangerSecondary"
                            disabled={removeSchedule.isLoading}
                            onClick={() => setRemove(true)}>
                            {t('actions.removeSchedule')}
                          </Button>
                        )}
                      </div>
                      <div className="flex flex-grid items-center gap-2">
                        <FormField
                          control={form.control}
                          name="confirmed"
                          render={({ field: { value, onChange } }) => (
                            <FormItemWrapper
                              className={cn('w-fit', schedule?.status === 'draft' && 'mt-7')}>
                              <div className="flex gap-2">
                                <Checkbox
                                  id="confirmed"
                                  checked={Boolean(value)}
                                  onCheckedChange={onChange}>
                                  {t('schedules.markAsConfirmed')}
                                </Checkbox>
                              </div>
                            </FormItemWrapper>
                          )}
                        />
                        <InternalScheduleActions
                          onCancel={() => {
                            form.reset();
                            setOpen(false);
                          }}
                          onSendSchedule={() => {
                            setValue('actionType', 'saveAndSend');
                            void form.handleSubmit(onSubmit)();
                          }}
                          onRescheduleAsDraft={() => {
                            setValue('status', 'draft');
                            setValue('actionType', '');
                            void form.handleSubmit(onSubmit)();
                          }}
                          onSaveAsDraft={() => setValue('actionType', '')}
                          locked={locked || !active.schedule.id}
                          scheduleId={schedule?.id}
                          foremanId={active.schedule.foreman?.id}
                          readOnly={readOnly}
                          isDraft={schedule?.status === 'draft'}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </form>
            {overlap && (
              <OverlapScheduleDialog
                open={overlap}
                setOpen={setOverlap}
                disabled={locked || !active.schedule.id}
                onCancel={() => setValue('forceOverlapping', false)}
                onConfirm={() => {
                  setValue('forceOverlapping', true);
                  void form.handleSubmit(onSubmit)();
                }}
                conflicts={conflicts || []}
                date={
                  active.schedule?.startsAt
                    ? shortDayDate(toTenantDate(active.schedule.startsAt))
                    : ''
                }
                name={active.schedule?.task?.name}
                summaryDetail={
                  active.schedule?.startsAt
                    ? `${[
                        formatTime(toTenantDate(active.schedule.startsAt)),
                        formatTime(toTenantDate(active.schedule?.endsAt || '')),
                      ].join(' - ')}, ${shortDayDate(toTenantDate(active.schedule.startsAt))}`
                    : ''
                }
              />
            )}
            <SpecialtyOverrideDialog
              open={specialtyOverride}
              setOpen={setSpecialtyOverride}
              disabled={locked || !active.schedule.id}
              onCancel={() => setValue('forceSpecialty', false)}
              onConfirm={() => {
                setValue('forceSpecialty', true);
                void form.handleSubmit(onSubmit)();
              }}
            />
          </Form>
        </DialogContent>
      </Dialog>
      <RemoveScheduleDialog
        open={remove}
        setOpen={setRemove}
        onConfirm={removeSchedule.mutate}
        disabled={removeSchedule.isLoading}
      />
    </>
  );
}
